import * as tf from "@tensorflow/tfjs";
// wasm-only build (no webgpu/jsep glue) — smaller, single pinned binary.
import * as ort from "onnxruntime-web/wasm";

// Single-threaded WASM avoids the COOP/COEP headers that SharedArrayBuffer
// (multi-thread) requires — CRA's dev server doesn't set those. The WASM
// binary is served locally from public/onnxruntime (copied from the installed
// package) so there's no CDN dependency or version drift.
ort.env.wasm.numThreads = 1;
ort.env.wasm.wasmPaths = "/onnxruntime/";

/**
 * TrackNet ball-tracking inference path (prototype).
 *
 * TrackNet is a purpose-built CNN for fast, small, motion-blurred balls: it
 * consumes N consecutive frames stacked on the channel axis and outputs a
 * heatmap whose peak is the ball location. This module wraps a TF.js-converted
 * TrackNet model with configurable I/O so you can point it at a converted model
 * without touching code.
 *
 * To get a model: convert a Keras/ONNX TrackNet(V2/V3) to TF.js with
 *   tensorflowjs_converter --input_format=keras model.h5 ./tfjs_tracknet
 * then serve the resulting `model.json` (+ weight shards) over HTTP/CORS and
 * paste its URL into the Ball Tracker Lab → TrackNet setup.
 */

const LS_KEY = "psts_tracknet_config";

export const defaultTrackNetConfig = {
  modelUrl: "",
  backend: "tfjs", // "tfjs" (model.json) | "onnx" (.onnx via onnxruntime-web)
  inputWidth: 512,
  inputHeight: 288,
  frames: 3, // consecutive frames stacked on the channel axis
  grayscale: false, // 1 channel/frame if true, else 3 (RGB)
  channelsFirst: false, // NCHW input layout if true, else NHWC
  reverseFrames: true, // stack newest-first (BallTrackerNet reference order)
  bgr: true, // feed BGR channel order (BallTrackerNet was trained on cv2/BGR)
  normalize255: true, // divide pixels by 255 (0..1); off = raw 0..255
  frameInterval: 1 / 30, // min video-time gap (s) between captured frames
  minBrightPx: 2, // reject detections smaller than this (single-pixel noise)
  heatmapThreshold: 0.5, // min peak value (auto-scaled if heatmap is 0..255)
  debug: false, // console-log input/output ranges + ball location
  // "heatmap": output is a spatial heatmap (argmax over H,W).
  // "argmax": output is class channels (e.g. BallTrackerNet's 256) — argmax
  //           over the channel axis yields the intensity map.
  outputMode: "heatmap",
};

export function loadTrackNetConfig() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw
      ? { ...defaultTrackNetConfig, ...JSON.parse(raw) }
      : { ...defaultTrackNetConfig };
  } catch {
    return { ...defaultTrackNetConfig };
  }
}

export function saveTrackNetConfig(cfg) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(cfg));
  } catch {
    /* ignore quota / private mode */
  }
}

export class TrackNet {
  constructor(config) {
    this.cfg = { ...defaultTrackNetConfig, ...config };
    this.model = null; // tf.js model
    this.session = null; // onnxruntime-web session
    this.frameBuf = []; // recent preprocessed frame tensors, each [H, W, C]
  }

  async load() {
    if (!this.cfg.modelUrl?.trim()) {
      throw new Error("No TrackNet model URL configured");
    }
    // The preprocessing (fromPixels/resize/concat/argMax) uses TF.js ops, so a
    // backend must be initialized first. Prefer WebGL for reliability; fall
    // back to whatever tf.ready() resolves to.
    try {
      await tf.setBackend("webgl");
    } catch {
      /* fall back to default backend */
    }
    await tf.ready();

    if (this.cfg.backend === "onnx") {
      this.session = await ort.InferenceSession.create(this.cfg.modelUrl, {
        executionProviders: ["wasm"],
      });
      return this;
    }
    // TF.js — conversions may be graph or layers models, try both.
    try {
      this.model = await tf.loadGraphModel(this.cfg.modelUrl);
    } catch (graphErr) {
      try {
        this.model = await tf.loadLayersModel(this.cfg.modelUrl);
      } catch (layersErr) {
        throw new Error(
          `Could not load as graph or layers model (${layersErr.message})`
        );
      }
    }
    return this;
  }

  reset() {
    this.frameBuf.forEach((t) => t.dispose());
    this.frameBuf = [];
    this.lastPushT = null;
  }

  /** Preprocess one frame from a video/canvas/image into a [H, W, C] tensor. */
  _grab(source) {
    return tf.tidy(() => {
      let img = tf.browser.fromPixels(source); // [H, W, 3]
      img = tf.image.resizeBilinear(img, [
        this.cfg.inputHeight,
        this.cfg.inputWidth,
      ]);
      if (this.cfg.grayscale) {
        const [r, g, b] = tf.split(img, 3, 2);
        img = r.mul(0.299).add(g.mul(0.587)).add(b.mul(0.114)); // [H, W, 1]
      } else if (this.cfg.bgr) {
        img = tf.reverse(img, [2]); // RGB -> BGR
      }
      return this.cfg.normalize255 ? img.div(255) : img;
    });
  }

  /**
   * Capture & preprocess the current frame into the rolling buffer.
   * Call this EVERY render tick so the stacked frames stay temporally
   * consecutive — TrackNet relies on short-range ball motion between frames.
   */
  pushFrame(source) {
    const srcW = source.videoWidth || source.width || source.naturalWidth;
    const srcH = source.videoHeight || source.height || source.naturalHeight;
    if (!srcW || !srcH) return;
    // Only capture when the video has advanced to a genuinely new frame, so the
    // stacked frames are distinct & consecutive (not 60fps duplicates of one
    // 30fps frame). ~1/30s spacing matches typical TrackNet training cadence.
    const t = typeof source.currentTime === "number" ? source.currentTime : null;
    const minGap = this.cfg.frameInterval ?? 1 / 30;
    if (
      t != null &&
      this.lastPushT != null &&
      this.frameBuf.length > 0 &&
      t - this.lastPushT < minGap * 0.9
    ) {
      return; // same frame as last capture — skip
    }
    this.lastPushT = t;
    this.srcW = srcW;
    this.srcH = srcH;
    this.frameBuf.push(this._grab(source));
    while (this.frameBuf.length > this.cfg.frames) {
      this.frameBuf.shift().dispose();
    }
  }

  /**
   * Run inference on the current buffer. Returns { x, y, score } in *source*
   * pixel coordinates, or null (no ball / still warming up). Pass a source to
   * also push it first (single-shot convenience).
   */
  async detect(source) {
    if (source) this.pushFrame(source);
    if (!this.model && !this.session) return null;
    const srcW = this.srcW;
    const srcH = this.srcH;
    if (!srcW || !srcH) return null;
    if (this.frameBuf.length < this.cfg.frames) return null; // warming up

    // Build the batched input tensor in the requested layout.
    const input = tf.tidy(() => {
      const frames = this.cfg.reverseFrames
        ? this.frameBuf.slice().reverse()
        : this.frameBuf;
      let x = tf.concat(frames, 2); // [H, W, frames*C]
      if (this.cfg.channelsFirst) x = x.transpose([2, 0, 1]); // [C, H, W]
      return x.expandDims(0); // add batch dim
    });

    // Run the model (TF.js or ONNX) → raw output data + dims (no big GPU copy).
    let rawData;
    let dims;
    try {
      if (this.cfg.backend === "onnx") {
        const inputData = await input.data(); // Float32Array (matches layout)
        const feeds = {
          [this.session.inputNames[0]]: new ort.Tensor(
            "float32",
            inputData,
            input.shape
          ),
        };
        const results = await this.session.run(feeds);
        const o = results[this.session.outputNames[0]];
        rawData = o.data;
        dims = o.dims;
        this._debugLog(inputData, input.shape, rawData, dims);
      } else {
        let out = this.model.predict(input);
        if (Array.isArray(out)) out = out[0];
        rawData = await out.data();
        dims = out.shape;
        this._debugLog(null, input.shape, rawData, dims);
        out.dispose();
      }
    } finally {
      input.dispose();
    }

    // Class-channel models (e.g. BallTrackerNet, 256 ch): localize on the CPU
    // straight from the logits — avoids allocating a ~236 MB GPU tensor/frame.
    if (this.cfg.outputMode === "argmax") {
      return this._ballFromClassChannels(rawData, dims, srcW, srcH);
    }

    // Heatmap models: reduce to a 2-D [H, W] map (these outputs are small).
    const heatmap = tf.tidy(() => {
      let hm = tf.tensor(rawData, dims).squeeze();
      if (hm.rank === 1) {
        hm = hm.reshape([this.cfg.inputHeight, this.cfg.inputWidth]);
      } else if (hm.rank === 3) {
        const [a, b, c] = hm.shape;
        if (a === 1) hm = hm.reshape([b, c]); // [1, H, W]
        else if (c === 1) hm = hm.reshape([a, b]); // [H, W, 1]
        else hm = hm.mean(2); // collapse channels [H, W, C]
      }
      return hm;
    });

    const data = await heatmap.data();
    const [hH, hW] = heatmap.shape;
    heatmap.dispose();

    // argmax over the heatmap
    let maxVal = -Infinity;
    let maxIdx = -1;
    for (let i = 0; i < data.length; i++) {
      if (data[i] > maxVal) {
        maxVal = data[i];
        maxIdx = i;
      }
    }
    if (maxIdx < 0) return null;

    // heatmaps may be 0..1 (sigmoid) or 0..255 — normalize the score
    let score = maxVal > 1.5 ? maxVal / 255 : maxVal;
    if (score < this.cfg.heatmapThreshold) return null;

    const hy = Math.floor(maxIdx / hW);
    const hx = maxIdx % hW;
    return {
      x: (hx / hW) * srcW,
      y: (hy / hH) * srcH,
      score: Math.min(1, score),
    };
  }

  /**
   * Ball localization for class-channel ("argmax") models, computed on the CPU
   * from the raw logits. The ball corresponds to the brightest predicted class,
   * so we spatial-argmax the summed top-K channel logits, then take the softmax
   * mass on those channels at that pixel as the confidence.
   */
  _ballFromClassChannels(data, dims, srcW, srcH) {
    const cf = this.cfg.channelsFirst;
    let C;
    let H;
    let W;
    if (dims.length === 4) {
      if (cf) {
        C = dims[1];
        H = dims[2];
        W = dims[3];
      } else {
        H = dims[1];
        W = dims[2];
        C = dims[3];
      }
    } else if (dims.length === 3) {
      if (cf) {
        C = dims[0];
        H = dims[1];
        W = dims[2];
      } else {
        H = dims[0];
        W = dims[1];
        C = dims[2];
      }
    } else {
      return null;
    }

    const HW = H * W;

    // Per-pixel argmax over the class channels → each pixel's predicted
    // intensity (0..C-1); the ball is the pixel with the brightest class.
    const bestClassPerPixel = new Int16Array(HW);
    if (cf) {
      // channel-major memory order: sequential, cache-friendly
      const bestVal = new Float32Array(HW);
      bestVal.fill(-Infinity);
      for (let c = 0; c < C; c++) {
        const off = c * HW;
        for (let p = 0; p < HW; p++) {
          const v = data[off + p];
          if (v > bestVal[p]) {
            bestVal[p] = v;
            bestClassPerPixel[p] = c;
          }
        }
      }
    } else {
      for (let p = 0; p < HW; p++) {
        const base = p * C;
        let mv = -Infinity;
        let mc = 0;
        for (let c = 0; c < C; c++) {
          const v = data[base + c];
          if (v > mv) {
            mv = v;
            mc = c;
          }
        }
        bestClassPerPixel[p] = mc;
      }
    }

    // ball = centroid of the near-peak (brightest-class) pixels
    let bestClass = -1;
    let bestP = -1;
    for (let p = 0; p < HW; p++) {
      const c = bestClassPerPixel[p];
      if (c > bestClass) {
        bestClass = c;
        bestP = p;
      }
    }

    const score = C > 1 ? bestClass / (C - 1) : 0;
    this.debugScore = score; // exposed for on-screen diagnostics
    if (bestP < 0 || score < this.cfg.heatmapThreshold) return null;

    // centroid of near-peak pixels within a LOCAL window around the brightest
    // pixel, so distant bright spots (court lines, graphics) don't drag it off.
    const near = Math.max(1, bestClass - 2);
    const bx = bestP % W;
    const by = (bestP / W) | 0;
    const R = Math.max(4, Math.round(W * 0.04));
    const y0 = Math.max(0, by - R);
    const y1 = Math.min(H - 1, by + R);
    const x0 = Math.max(0, bx - R);
    const x1 = Math.min(W - 1, bx + R);
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (let y = y0; y <= y1; y++) {
      const row = y * W;
      for (let x = x0; x <= x1; x++) {
        if (bestClassPerPixel[row + x] >= near) {
          sx += x;
          sy += y;
          n++;
        }
      }
    }
    const cx = n ? sx / n : bx;
    const cy = n ? sy / n : by;

    // throttled diagnostic: where is the ball + how big is the bright region?
    const t2 = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (this.cfg.debug && (!this._lastLog2 || t2 - this._lastLog2 > 1000)) {
      this._lastLog2 = t2;
      // eslint-disable-next-line no-console
      console.log(
        "[TrackNet] ball peakClass=%d score=%s brightPx=%d centroid=(%d,%d) of %dx%d",
        bestClass,
        score.toFixed(2),
        n,
        cx | 0,
        cy | 0,
        W,
        H
      );
    }

    // reject tiny (single-pixel) detections — these are noise, not the ball
    if (n < (this.cfg.minBrightPx ?? 1)) return null;

    return {
      x: (cx / W) * srcW,
      y: (cy / H) * srcH,
      score: Math.min(1, score),
    };
  }

  // Log input/output value ranges ~once per second for diagnostics.
  _debugLog(inputData, inShape, rawData, dims) {
    if (!this.cfg.debug) return;
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (this._lastLog && now - this._lastLog < 1000) return;
    this._lastLog = now;
    const range = (a) => {
      if (!a || !a.length) return [null, null];
      let mn = Infinity;
      let mx = -Infinity;
      const step = Math.max(1, Math.floor(a.length / 50000));
      for (let i = 0; i < a.length; i += step) {
        const v = a[i];
        if (v < mn) mn = v;
        if (v > mx) mx = v;
      }
      return [mn, mx];
    };
    const [imn, imx] = range(inputData);
    const [omn, omx] = range(rawData);
    // eslint-disable-next-line no-console
    console.log(
      "[TrackNet] mode=%s in%o[%s..%s] out%o[%s..%s]",
      this.cfg.outputMode,
      inShape,
      imn,
      imx,
      dims,
      omn,
      omx
    );
  }

  dispose() {
    this.reset();
    try {
      this.model?.dispose?.();
    } catch {
      /* noop */
    }
    try {
      this.session?.release?.();
    } catch {
      /* noop */
    }
    this.model = null;
    this.session = null;
  }
}
